import { DateTime } from 'luxon';
import { memo, ReactElement, useCallback, useEffect, useState } from 'react';
import { SessionStatusLayout } from './SessionStatusLayout';
import { SessionTime } from './SessionTime';
import { SessionLogoutButton } from './SessionLogoutButton';
import { SessionExtendDialog } from './SessionExtendDialog';
import { SessionLogoutDialog } from './SessionLogoutDialog';

export interface SessionStatusProps {
  session_started_at: DateTime;
  session_end_at?: DateTime;
  onSessionExit?: () => Promise<void>;
  onSessionExtend?: () => Promise<void>;
}

export const SessionStatus = memo(function SessionStatus({
  session_started_at,
  session_end_at,
  onSessionExit,
  onSessionExtend,
}: SessionStatusProps): ReactElement {
  const { remainingTime, askForExtend } = useRemainingTime(session_end_at);
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [dialogDismissed, setDialogDismissed] = useState(false);

  // Show extend dialog when less than one minute remaining and extend handler is available
  useEffect(() => {
    if (askForExtend && onSessionExtend && !showExtendDialog && !dialogDismissed) {
      setShowExtendDialog(true);
    }
  }, [askForExtend, onSessionExtend, showExtendDialog, dialogDismissed]);

  const handleExtendSession = useCallback(
    function handleExtendSession() {
      setShowExtendDialog(false);
      onSessionExtend?.();
    },
    [onSessionExtend]
  );

  const handleCloseDialog = useCallback(function handleCloseDialog() {
    setShowExtendDialog(false);
    setDialogDismissed(true);
  }, []);

  const handleLogoutClick = useCallback(function handleLogoutClick() {
    setShowLogoutDialog(true);
  }, []);

  const handleConfirmLogout = useCallback(
    async function handleConfirmLogout() {
      setShowLogoutDialog(false);
      if (onSessionExit) {
        await onSessionExit();
      }
    },
    [onSessionExit]
  );

  const handleCloseLogoutDialog = useCallback(function handleCloseLogoutDialog() {
    setShowLogoutDialog(false);
  }, []);

  return (
    <SessionStatusLayout
      sessionTime={<SessionTime sessionStartedAt={session_started_at} remainingTime={remainingTime} />}
      logoutButton={onSessionExit ? <SessionLogoutButton onSessionExit={handleLogoutClick} /> : undefined}
      extendDialog={
        onSessionExtend ? (
          <SessionExtendDialog
            isOpen={showExtendDialog}
            onExtendSession={handleExtendSession}
            onCloseDialog={handleCloseDialog}
          />
        ) : undefined
      }
      logoutDialog={
        onSessionExit ? (
          <SessionLogoutDialog
            isOpen={showLogoutDialog}
            onConfirmLogout={handleConfirmLogout}
            onCloseDialog={handleCloseLogoutDialog}
          />
        ) : undefined
      }
    />
  );
});

interface RemainingTime {
  remainingTime: string | null;
  askForExtend: boolean;
}

function useRemainingTime(endTime?: DateTime): RemainingTime {
  const [remainingTime, setRemainingTime] = useState<string | null>(null);
  const [expiresSoon, setExpiresSoon] = useState(false);

  useEffect(() => {
    if (!endTime) {
      setRemainingTime(null);
      setExpiresSoon(false);
      return;
    }

    function updateRemainingTime() {
      if (!endTime) return;

      const now = DateTime.now();
      const diff = endTime.diff(now, ['minutes', 'seconds']).toObject();
      const minutes = Math.max(0, Math.floor(diff.minutes ?? 0));
      const seconds = Math.max(0, Math.floor(diff.seconds ?? 0));

      setExpiresSoon(minutes < 1 && seconds > 0);

      if (minutes === 0 && seconds === 0) {
        setRemainingTime('00:00');
        setExpiresSoon(false);
        return;
      }

      const pad = (n: number) => n.toString().padStart(2, '0');
      setRemainingTime(`${pad(minutes)}:${pad(seconds)}`);
    }

    updateRemainingTime();

    // Set up interval to update every second
    const interval = setInterval(updateRemainingTime, 1000);

    return () => clearInterval(interval);
  }, [endTime]);

  return { remainingTime, askForExtend: expiresSoon };
}
